import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../services/api";
import ReviewedAttendanceImport from './ReviewedAttendanceImport';
const field =
  "mt-1 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm";
const duration = (v) => {
  const n = Number(v || 0);
  return `${Math.floor(n / 3600)}h ${Math.floor((n % 3600) / 60)}m ${Math.floor(n % 60)}s`;
};
export default function ConnectorPreview({ device, accessToken, refresh = 0, approvalPage = false,initialUserId='',initialDate='' }) {
  const [status, setStatus] = useState(null),
    [user, setUser] = useState(""),
    [date, setDate] = useState(""),
    [preview, setPreview] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const generation = useRef(0),
    lock = useRef(false);
  useEffect(() => {
    generation.current++;
    setPreview(null);
    setUser(initialUserId);
    setStatus(null);
    setDate(
      initialDate || (device.code === "DEV-MDB-TD-PREVIEW"
        ? "2026-10-20"
        : new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(
            new Date(),
          )),
    );
  }, [device.id, device.code, accessToken,initialUserId,initialDate]);
  useEffect(() => {
    let active = true;
    generation.current++;
    setPreview(null);
    setError("");
    setBusy(false);
    lock.current = false;
    apiRequest(`/biometric-devices/${device.id}/connector-status`, {
      accessToken,
    })
      .then((r) => {
        if (active) {
          setStatus(r);
          setUser((current) =>
            (r.users || []).some((u) => u.deviceUserId === current)
              ? current
              : r.users?.[0]?.deviceUserId || "",
          );
        }
      })
      .catch((e) => {
        if (active)
          setError(e.message || "Unable to load connector preview status");
      });
    return () => {
      active = false;
      generation.current++;
    };
  }, [device.id, accessToken, refresh]);
  function change(fn, v) {
    generation.current++;
    setPreview(null);
    setError("");
    fn(v);
  }
  async function inspect(e) {
    e.preventDefault();
    if (lock.current || !user || !date) return;
    const ticket = generation.current;
    lock.current = true;
    setBusy(true);
    setError("");
    setPreview(null);
    try {
      const r = await apiRequest(
        `/biometric-devices/${device.id}/connector-preview?deviceUserId=${encodeURIComponent(user)}&date=${encodeURIComponent(date)}`,
        { accessToken },
      );
      if (ticket === generation.current) setPreview(r);
    } catch (e) {
      if (ticket === generation.current)
        setError(e.message || "Unable to inspect this day");
    } finally {
      lock.current = false;
      if (ticket === generation.current) setBusy(false);
    }
  }
  return (
    <section id="punch-day-preview" className="my-6 space-y-5 rounded-2xl border border-blue-200 bg-white p-5">
      <div>
        <h3 className="text-xl font-bold text-slate-900">
          {approvalPage?'Punches and proposed work sessions':'Connector preview punches'}
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          Source punches are retained separately from attendance. Directions and
          worked time below are provisional. {approvalPage?'Automatic processing starts 28 October 2026 when enabled; earlier dates require review. This page never runs payroll.':'Inspect source evidence here; use Punch Review & Approval for historical approvals and exceptions.'}
        </p>
      </div>
      {error && (
        <p
          role="alert"
          className="rounded-lg bg-red-50 p-3 text-sm text-red-800"
        >
          {error}
        </p>
      )}
      <p className="text-sm text-slate-600">
        Retained preview punches: <strong>{status?.punches ?? "—"}</strong> ·
        Connectors: {status?.connectors?.length ?? "—"}. These counts are not
        the live-punch count elsewhere on this page.
      </p>
      <form onSubmit={inspect} className="grid gap-4 md:grid-cols-3">
        <label className="text-sm font-semibold">
          Preview machine User ID
          <select
            required
            className={field}
            value={user}
            disabled={busy}
            onChange={(e) => change(setUser, e.target.value)}
          >
            <option value="">Choose retained machine ID</option>
            {(status?.users || []).map((u) => (
              <option key={u.deviceUserId} value={u.deviceUserId}>
                {u.deviceUserId} · {u._count} punches
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-semibold">
          Preview India calendar date
          <input
            required
            type="date"
            className={field}
            value={date}
            disabled={busy}
            onChange={(e) => change(setDate, e.target.value)}
          />
        </label>
        <button
          className="self-end rounded-xl bg-blue-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-40"
          disabled={busy || !user || !date}
        >
          {busy ? "Loading preview…" : "View day preview"}
        </button>
      </form>
      {status && !(status.users || []).length && (
        <p className="text-sm text-slate-500">
          No connector-preview punches are retained for this device yet.
        </p>
      )}
      {preview && (
        <div className="space-y-4">
          <div className="rounded-xl bg-slate-50 p-4">
            <p className="font-semibold">
              {preview.date} · Machine ID {preview.deviceUserId} ·{" "}
              {preview.employee
                ? `${preview.employee.employeeCode} · ${preview.employee.fullName}`
                : "Unmapped — HR must review a dated employee link"}
            </p>
            <p className="mt-2 text-sm">
              Worked (provisional): {duration(preview.workedSeconds)} · Unpaid
              gaps: {duration(preview.unpaidSeconds)} · Status: {preview.status}{" "}
              · Direction: {preview.directionSource}
            </p>
            {preview.openPunch && (
              <p className="mt-2 text-sm text-amber-900">
                Unmatched/open punch: {preview.openPunch.time}. No checkout has
                been invented.
              </p>
            )}
          </div>
          {[...(preview.mappingFlags || []), ...(preview.flags || [])].map(
            (f) => (
              <p key={f} className="text-sm font-semibold text-amber-900">
                {f.replaceAll("_", " ")}
              </p>
            ),
          )}
          {!!preview.ignoredRepeatedScans?.length&&<p className="rounded-xl bg-blue-50 p-3 text-sm text-blue-900">CCPL-1 repeated-scan rule: {preview.ignoredRepeatedScans.length} additional scan(s) within five seconds of the first scan are excluded from session pairing. All raw punches remain below.</p>}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr>
                  <th className="p-2">Source time (India)</th>
                  <th className="p-2">Table / record</th>
                  <th className="p-2">Original direction</th>
                  <th className="p-2">Session pairing</th>
                </tr>
              </thead>
              <tbody>
                {(preview.rawPunches || []).map((p) => (
                  <tr key={p.id} className="border-t">
                    <td className="p-2">{p.localTimestamp}</td>
                    <td className="p-2">
                      {p.sourceTable} / {p.sourceId}
                    </td>
                    <td className="p-2">
                      {p.direction ||
                        p.attDirection ||
                        "Blank — not supplied by machine"}
                    </td>
                    <td className="p-2">{preview.ignoredRepeatedScans?.some(r=>r.id===p.id)?'Repeated scan — excluded':'Retained'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!(preview.rawPunches || []).length && (
            <p className="text-sm">No retained preview punches on this date.</p>
          )}
          {approvalPage && ((preview.sessions||[]).length>0 || preview.openPunch) && <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <h4 className="font-semibold text-slate-900">Proposed employee work sessions</h4>
            <p className="mt-1 text-xs text-slate-600">Pairs 1–2, 3–4. Gaps are unpaid. Open sessions never receive an invented checkout.</p>
            <div className="mt-3 overflow-x-auto"><table className="w-full text-left text-sm"><thead><tr><th className="p-2">Session</th><th className="p-2">IN · India</th><th className="p-2">OUT · India</th><th className="p-2">Worked</th></tr></thead><tbody>
              {(preview.sessions||[]).map((s,i)=><tr key={s.inPunchId||i} className="border-t"><td className="p-2">{i+1}</td><td className="p-2">{s.in}</td><td className="p-2">{s.out}</td><td className="p-2">{duration(s.seconds)}</td></tr>)}
              {preview.openPunch&&<tr className="border-t text-amber-900"><td className="p-2">{(preview.sessions||[]).length+1}</td><td className="p-2">{preview.openPunch.time}</td><td className="p-2">Open — missing OUT</td><td className="p-2">Not counted yet</td></tr>}
            </tbody></table></div>
          </div>}
          {(preview.warnings || []).map((w, i) => (
            <p key={i} className="text-xs text-slate-500">
              {w}
            </p>
          ))}
          {approvalPage?(date<(status?.attendancePolicy?.automaticFrom || '2026-10-28')?<ReviewedAttendanceImport key={`${device.id}:${user}:${date}:${accessToken}:${refresh}`} deviceId={device.id} deviceUserId={user} date={date} accessToken={accessToken}/>:<div className="rounded-xl border bg-blue-50 p-4 text-sm">{status?.attendancePolicy?.enabled?'Normal mapped punches on or after the automatic start date are processed automatically into employee work sessions. No routine approval is needed. Check the exception queue for blocked or incomplete days.':'Automatic attendance is disabled on this server. An administrator must deploy and enable it before these punches can be processed automatically.'} Existing manual attendance is never overwritten.</div>):<a className="inline-block rounded-lg bg-blue-900 px-4 py-2 text-sm text-white" href={`/admindashboard/attendance/punch-review?device=${encodeURIComponent(device.id)}`}>Open Punch Review &amp; Approval</a>}
        </div>
      )}
    </section>
  );
}
